import { domain } from 'cesg-shared/dist/models';
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import type {
  CesgOrganisationStakeholder,
  ProjectOrganisationCesgDataProps,
} from '../sub-components/project-organisation-cesg/types';
import type { ProjectOrganisationExternalDataProps } from '../sub-components/project-organisation-external/types';
import type {
  ProjectOrganisationsActionEvent,
  ProjectOrganisationsProps,
} from '../types';

export type UseProjectOrganisationsLogicProps = ProjectOrganisationsProps;

const ORGANISATION_DISPLAY_ORDER = [
  domain.ProjectOrganisationType.CESG,
  domain.ProjectOrganisationType.CUSTOMER,
  domain.ProjectOrganisationType.UE,
  domain.ProjectOrganisationType.PROJEKTORER,
] as string[];

export const useProjectOrganisationsLogic = (
  props: UseProjectOrganisationsLogicProps,
  emit: (event: ProjectOrganisationsActionEvent) => void,
) => {
  const { t } = useI18n();

  const getLabel = (name: string) =>
    t(`overview.projectOrganisations.organisationTypes.${name}`, name);

  const sorted = computed(() =>
    props.organisations.slice().sort((a, b) => {
      const ai = ORGANISATION_DISPLAY_ORDER.indexOf(a.name);
      const bi = ORGANISATION_DISPLAY_ORDER.indexOf(b.name);
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
    }),
  );

  const cesg = computed<
    (ProjectOrganisationCesgDataProps & { id: string }) | null
  >(() => {
    const cesgOrg = sorted.value.find((o) => o.name === 'CESG') ?? null;
    if (!cesgOrg) return null;
    return {
      id: cesgOrg.id,
      organisationId: cesgOrg.id,
      projectId: props.projectId,
      name: getLabel(cesgOrg.name),
      stakeholders: cesgOrg.stakeholders.map(
        (s): CesgOrganisationStakeholder => ({
          id: s.id,
          order: s.order,
          name: s.name,
          email: s.email,
          phone: s.phone,
          role: s.role,
          userId: s.userId,
        }),
      ),
    };
  });

  const external = computed<
    (ProjectOrganisationExternalDataProps & { id: string })[]
  >(() =>
    sorted.value
      .filter((o) => o.name !== 'CESG')
      .map((o) => ({
        id: o.id,
        organisationId: o.id,
        projectId: props.projectId,
        name: getLabel(o.name),
        stakeholders: o.stakeholders,
      })),
  );

  const state = reactive({
    projectId: computed(() => props.projectId),
    organisations: reactive({ cesg, external }),
  });

  const action = {
    onAction: (event: ProjectOrganisationsActionEvent) => emit(event),
  };

  return {
    state,
    action,
  };
};
